import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Tabs, { TABS_SIZES, TABS_THEMES } from '@wg/wows-react-uikit/components/Tabs/Tabs';

import { root } from '~/preloaded';
import settings from '~/settings';
import { CBATTLES_TABS } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import useMount from '~/hooks/useMount';

import { ROUTES_MAP } from '~/router/routes';
import { useAppDispatch, useAppSelector } from '~/store';
import { currentAccountClanIdSelector } from '~/store/selectors/currentAccountSelector';
import { isLoggedIn } from '~/utils/auth';
import { checkClanBattlesThunk } from '~/Actions/ActionClanBattles';
import { getWinners } from '~/Actions/ActionHallFame';

import { AnimatedFetchingComponent } from '~/UIKit';

import styles from './ViewClanBattles.scss';

import type { Tab } from '@wg/wows-react-uikit/components/Tabs/Tabs';
import type { RootState } from '~/store';

const getCurrentTab = (pathname: string) => {
  if (pathname.startsWith(`${root}${ROUTES_MAP.CBATTLES_HISTORY}`)) {
    return CBATTLES_TABS.HISTORY;
  }
  if (pathname.startsWith(`${root}${ROUTES_MAP.CBATTLES_RULES}`)) {
    return CBATTLES_TABS.RULES;
  }
  if (pathname.startsWith(`${root}${ROUTES_MAP.CBATTLES_HALL}`)) {
    return CBATTLES_TABS.HALL;
  }
  if (pathname.startsWith(`${root}${ROUTES_MAP.CBATTLES_RATING}`)) {
    return CBATTLES_TABS.RATING;
  }

  return CBATTLES_TABS.HISTORY;
};

const stateSelector = (state: RootState) => {
  return {
    isFetching: state.ReducerClanBattles.isFetching,
    winners: state.ReducerHallFame?.winnersList,
  };
};

const ViewClanBattles: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { isFetching, winners } = useAppSelector(stateSelector, shallowEqual);
  const clanId = useAppSelector(currentAccountClanIdSelector);

  useMount(() => {
    dwhExport.push(DWH_EVENTS.CLAN_BATTLES.OPEN_PAGE);

    if (isLoggedIn()) {
      void dispatch(checkClanBattlesThunk());
    }
    dispatch(getWinners());
  });

  const [currentTab, setCurrentTab] = useState(getCurrentTab(location.pathname));
  useEffect(() => {
    setCurrentTab(getCurrentTab(location.pathname));
  }, [location.pathname]);

  const isHallOfFameAvailable = useMemo(() => {
    return settings.ladder.hallOfFameIsEnabled && !isEmpty(winners);
  }, [winners]);

  const tabs: Tab<CBATTLES_TABS>[] = useMemo(() => {
    const tabs = [
      { value: CBATTLES_TABS.HISTORY, content: t('Клановые бои') },
      { value: CBATTLES_TABS.RATING, content: t('Рейтинг кланов') },
    ];

    if (isHallOfFameAvailable) {
      tabs.push({ value: CBATTLES_TABS.HALL, content: t('Зал славы') });
    }

    if (clanId) {
      tabs.push({ value: CBATTLES_TABS.RULES, content: t('Правила') });
    }

    return tabs;
  }, [clanId, isHallOfFameAvailable]);

  const onToggleTab = useCallback(
    (value: CBATTLES_TABS) => {
      navigate(`${root}${ROUTES_MAP.CBATTLES}/${value}`, { replace: true });
    },
    [navigate],
  );

  return (
    <AnimatedFetchingComponent className={styles.wrapper} isFetching={isFetching}>
      <div className={classNames(styles.tabsNavigation, isHallOfFameAvailable && styles.gold)}>
        <Tabs
          classNameWrapper={styles.tabs}
          theme={TABS_THEMES.CLIENT}
          size={TABS_SIZES.M}
          isUppercase
          smallHeight
          tabSelected={currentTab}
          tabs={tabs}
          onToggleTab={onToggleTab}
        />
      </div>
      <Outlet />
    </AnimatedFetchingComponent>
  );
};

export default memo(ViewClanBattles);
